/* eslint-disable */
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { first } from 'rxjs/operators';
import { MSRemoteLoggerCategory } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';
import { environment } from 'src/environments/environment';
import { PermissionsError } from '../models/error.model';
import { DebugLoggingCapsule } from '../use-case/feature-flags/capsule/debug-logging.capsule';
import { MSFeatureFlags } from '../use-case/feature-flags/feature-flags';
import { TranslationUseCase } from '../use-case/translation/translation.use-case';
import { UserStorageUseCase } from '../use-case/user-storage/user-storage.use-case';
import { getSentryConfigurationJson } from '../wrappers/sentry.wrapper';
import { CONSTANTS } from './config.service';
import { DateTimeService } from './date-time/date-time.service';
import { GisService } from './gis.service';
import { LoggerService } from './logger.service';
import { PosterService } from './poster.service';
import { PrefsService } from './prefs.service';
import { PubSubService } from './pub-sub.service';
import { StudyService } from './study.service';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root'
})
export class GeowatcherService {
  bgGeo: any;

  constructor(
    private platform: Platform,
    private logger: LoggerService,
    private pubSub: PubSubService,
    private dateTime: DateTimeService,
    private gis: GisService,
    private utils: UtilsService,
    private prefs: PrefsService,
    private translationUseCase: TranslationUseCase,
    private study: StudyService,
    private poster: PosterService,
    private userStorage: UserStorageUseCase,
    private debugLoggingCapsule: DebugLoggingCapsule
  ) {}

  async initialize(baseConfig) {
    if (!CONSTANTS.IS_DEVICE) return;
    this.bgGeo = window['plugins'] ? window['plugins'].backgroundGeoLocation : undefined;
    if (!this.bgGeo) return;

    await this.setFeatureFlags();
    await this.configure(baseConfig);
  }

  async getCurrentTranslations(configuration: any, deviceType: string) {
    const translations = await this.translationUseCase.getCurrentLanguageTranslations().pipe(
      first()
    ).toPromise();
    const deviceTranslations = this.getBackendText(translations, deviceType);

    return this.utils.getPromise(this.bgGeo, this.bgGeo.configure, [configuration, deviceTranslations]);
  }

  getCurrentTripId() {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.getCurrentTripId);
  }
  updateHasSurvey(trips) {
    this.debugLoggingCapsule.logIfAble(`updateHasSurvey: ${trips.length} trips`);

    const getTripId = (trip) => trip.id;
    return this.utils.getPromise(this.bgGeo, this.bgGeo.updateHasSurvey, [trips.map(getTripId)]);
  }
  clearHasSurvey(tripIds) {
    // for dev
    if (this.platform.is('android')) {
      return this.utils.getPromise(this.bgGeo, this.bgGeo.clearHasSurvey, [tripIds]);
    } else {
      return Promise.resolve("ignored - unimpl'd");
    }
  }
  hideFromUi(trips) {
    const getTripId = (trip) => trip.id;
    return this.utils.getPromise(this.bgGeo, this.bgGeo.hideFromUi, [trips.map(getTripId)]);
  }
  setBadge() {
    if (this.bgGeo) {
      return this.utils.getPromise(this.bgGeo, this.bgGeo.setBadge);
    }
  }
  deleteAllTrips() {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.deleteAllTrips);
  }
  getCurrentTripDetails() {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.getCurrentTripDetails);
  }
  hasUnsentData() {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.hasUnsentData);
  }
  //tripStartsWithinStudy(tripId) {                         return this.utils.getPromise(this.bgGeo, this.bgGeo.tripStartsWithinStudy,        [tripId]); }

  getUnsurveyedTrips() {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.getUnsurveyedTrips);
  }
  getAllTrips() {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.getAllTrips);
  }
  hasTrips() {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.hasTrips);
  }
  queueSurveyForSending(surveyId) {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.queueSurveyForSending, [surveyId]);
  }
  queueDailySurveyForSending(surveyId) {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.queueDailySurveyForSending, [surveyId]);
  }
  queueProxySurveyForSending(surveyId) {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.queueProxySurveyForSending, [surveyId]);
  }
  getPointsForTimes(startTime, stopTime, includeTime, includeSplits = false, includeReverses = false) {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.getPointsForTimes, [
      startTime,
      stopTime,
      includeTime,
      includeSplits,
      includeReverses
    ]);
  }
  getSplits(tripId) {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.getSplits, [tripId]);
  }
  getReverses(tripId) {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.getReverses, [tripId]);
  }
  unregisterBreadcrumbs() {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.unregisterBreadcrumbs);
  }
  // TODO - is this for apple too? or android only?
  recordSpuriousStart(intLat, intLon) {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.recordSpuriousStart, [
      this.gis.getFloatLatOrLon(intLat),
      this.gis.getFloatLatOrLon(intLon)
    ]);
  }
  testEvent(args) {
    return this.bgGeo.testEvent(args);
  }
  uploadData() {
    this.bgGeo.uploadData();
  }
  getPoints(tripId, success, failure) {
    this.bgGeo.getPoints(tripId, success, failure);
  }
  sendSurveys() {
    this.bgGeo.sendSurveys();
  }
  setSurveySources(dbName, tripSurveyTableName, dailySurveyTableName) {
    this.bgGeo.setSurveySources(dbName, tripSurveyTableName, dailySurveyTableName);
  }
  countTodo() {
    if (this.bgGeo) {
      return this.utils.getPromise(this.bgGeo, this.bgGeo.countTodo);
    }
  }
  countTodoPerDay() {
    if (this.bgGeo) {
      return this.utils.getPromise(this.bgGeo, this.bgGeo.countTodoPerDay);
    }
  }
  closeTrip() {
    return this.utils.getPromise(this.bgGeo, this.bgGeo.closeTrip);
  }

  debugging_saveActivities() {
    const now = this.dateTime.getNowSeconds();
    const start = now - 12 * 60 * 60;
    const stop = now;
    return this.utils.getPromise(this.bgGeo, this.bgGeo.DEBUGGING_saveModes, [start, stop]);
  }

  getBackendText(translations, deviceType) {
    let text = {};
    const prefix = 'BACKENDS_' + deviceType + '_';

    Object.keys(translations).forEach((k) => {
      if (k.startsWith(prefix)) {
        const newKey = k.replace(prefix, '');
        text = Object.assign(text, { [newKey]: translations[k] });
      }
    });
    return text;
  }

  private async configure(baseConfig) {
    const serverAddress = await this.userStorage.getServerAddress().toPromise();
    return this.prefs
      .getPromises('authCode', 'activationCode', 'phases', 'personId', 'proxiedCount')
      .then((responses) => {
        const configuration = Object.assign(
          {
            debug: true,
            authCode: responses[0],
            activationCode: responses[1],
            SERVER_URL: serverAddress,
            studyStartTime: this.study.getStartSeconds(),
            studyStopTime: this.study.getStopSeconds(),
            uploadStopTime: this.study.getUploadStopSeconds(),
            frontEndDbName: CONSTANTS.mainFrontEndDbName,
            tripSurveyTableName: CONSTANTS.tripSurveyTableName,
            dailySurveyTableName: CONSTANTS.dailySurveyTableName,
            proxySurveyTableName: CONSTANTS.proxySurveyTableName,
            phases: JSON.stringify(responses[2]),
            respondentPersonId: responses[3],
            proxiedCount: responses[4],
            isProduction: environment.production,
            sentryConfiguration: this.platform.is('ios') ? getSentryConfigurationJson() : null,
          },
          baseConfig
        );

        // logger and event listener may not finish before we return, but that shouldn't be a problem
        this.bgGeo.startLogger((params) => {
          const msg = params.message;
          this.logger.log(msg, MSRemoteLoggerCategory.GEOWATCHER_SERVICE);
        });
        this.bgGeo.startEventListener((pubSubArgs) => {
          // just a pass-through; no attempt to keep front and back ends in sync here
          this.logger.log('pubSubArgs', MSRemoteLoggerCategory.GEOWATCHER_SERVICE, pubSubArgs);
          this.pubSub.publish.apply(this.pubSub, pubSubArgs);
        });

        const deviceType = this.platform.is('android') ? 'ANDROID' : 'IOS';
        if (deviceType === 'IOS') {
          return this.getCurrentTranslations(configuration, deviceType);
        }

        return this.bgGeo
          .requestPermissions()
          .then(() => this.getCurrentTranslations(configuration, deviceType))
          .catch((err) => {
            const permissions = err.permissions as string[];
            const error = new PermissionsError(permissions);

            this.logger.error('permission-error', MSRemoteLoggerCategory.GEOWATCHER_SERVICE, err);
            throw error
          });
      });
  }

  private async setFeatureFlags() {
    const featureFlags = MSFeatureFlags.get();
    await this.bgGeo.setFeatureFlags(featureFlags);
  }
}
